import groupBy from 'lodash/groupBy'
import sortBy from 'lodash/sortBy'
import toArray from 'lodash/toArray'
import { computed, makeObservable } from 'mobx'

import { Model } from 'modules/core/lib/mobx-rest'

import { NumericFieldTypes } from 'modules/custom_resources/store/collections/customFields/fields'

export interface Question {
  id: number
  position: number
  category_name?: string
  category_id?: number
  label: string
  type: string
  min_value?: number
  max_value?: number
  options?: string[]
}

export interface Answers {
  [questionId: number]: string | number
}

export interface AnalyticQuestionGroup {
  id: number
  target_id: number
  participant_id: number | null
  answers: Answers
  created_at: string // Date in backend format
  answered_at: string // Date in backend format
  end_on: string // Date in backend format
  editable_until: string // DateTime in backend format
}
interface Category {
  name: string
  questionsIds: Set<number>
}

export type Cycle = [string, string] // [start, end], in backend format
const NONE_CATEGORY = 0

interface IAnalyticResult {
  [key: string]: any
}

class AnalyticResult extends Model<IAnalyticResult> {
  constructor(attr: IAnalyticResult = {}) {
    super(attr)
    makeObservable(this, {
      typeOfReviewer: computed,
      questionGroups: computed,
      questions: computed,
      questionIds: computed,
      categories: computed,
      numericQuestions: computed,
      numericQuestionIds: computed,
      numericCategories: computed,
      cycles: computed,
    })
  }

  static urlRoot() {
    return '/surveys/analytics'
  }

  get typeOfReviewer(): string {
    return this.attributes.type_of_reviewer
  }

  get questions(): Set<Question> {
    const groupedQuestions = groupBy(
      this.attributes.questions,
      (q) => q.category_id || NONE_CATEGORY
    )
    const questions = toArray(groupedQuestions).flatMap((questions) =>
      sortBy(questions, 'position')
    )

    return new Set(questions)
  }

  get questionIds(): Set<number> {
    return new Set(Array.from(this.questions).map((q: Question) => q.id))
  }

  get numericQuestions(): Set<Question> {
    const numericFuildTypes = Object.values(NumericFieldTypes)
    const numericQuestions = new Set<Question>()
    this.questions.forEach((question: Question) => {
      if (numericFuildTypes.includes(question.type)) {
        numericQuestions.add(question)
      }
    })

    return numericQuestions
  }

  get numericQuestionIds(): Set<number> {
    return new Set(Array.from(this.numericQuestions).map((q: Question) => q.id))
  }

  get questionGroups(): AnalyticQuestionGroup[] {
    return this.attributes.question_groups.map((questionGroup: any) => {
      const answers = questionGroup.answers.reduce((memo: Answers, answer: any) => {
        memo[answer.question_id] = answer.value

        return memo
      }, {})

      return {
        ...questionGroup,
        answers,
      }
    })
  }

  get categories(): Record<number, Category> {
    return this.groupCategories(this.questions)
  }

  get numericCategories(): Record<number, Category> {
    return this.groupCategories(this.numericQuestions)
  }

  groupCategories(questions: Set<Question>): Record<number, Category> {
    const memo: Record<number, Category> = {}

    questions.forEach((question) => {
      const categoryId = question.category_id || NONE_CATEGORY

      if (!memo[categoryId]) {
        memo[categoryId] = {
          name: question.category_name || '',
          questionsIds: new Set([question.id]),
        }
      }

      memo[categoryId]?.questionsIds.add(question.id)
    })

    return memo
  }

  // TODO [people-talent]: cycles need a proper refactor to avoid hacks like this!
  // This is not ideal since we should have cycles modeled better. But since who holds the
  // information of start / end of a question is the question itself, we need to iterate them checking
  // that information
  get cycles(): Set<Cycle> {
    return new Set(
      this.attributes.question_groups.map((questionGroup: AnalyticQuestionGroup) => [
        questionGroup.created_at,
        questionGroup.end_on,
      ])
    )
  }

  questionGroupsInCycle(cycle: Cycle | null): AnalyticQuestionGroup[] {
    if (!cycle) return this.questionGroups

    return this.questionGroups.filter(({ end_on }: AnalyticQuestionGroup) => {
      const [, end] = cycle

      return end_on === end
    })
  }
}

export default AnalyticResult
